import React from 'react';
import { useDroppable } from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CrmField } from './DraggableCrmField';
import { ContactRecord } from 'src/services/integration-app.service';
import { Label } from 'src/components/Label';
import { cn } from 'src/utils/className';
import { MappedFieldItem } from 'src/pages/Automations/components/FieldMapping/MappedFieldItem';
import { useFieldMappingDnd } from 'src/pages/Automations/components/FieldMapping/FieldMappingDndContext';

interface DropZoneProps {
  campaignField: string;
  fieldLabel: string;
  mappedFields: CrmField[];
  sampleContacts?: ContactRecord[];
  disabled?: boolean;
  required?: boolean;
  onFieldRemoved?: (campaignField: string, crmFieldValue: string) => void;
}

export const DropZone: React.FC<DropZoneProps> = ({
  campaignField,
  fieldLabel,
  mappedFields,
  sampleContacts = [],
  disabled = false,
  required = false,
  onFieldRemoved,
}) => {
  const { setNodeRef, isOver } = useDroppable({
    id: campaignField,
  });

  const { activeSourceContainer, overContainerId, insertIndex } =
    useFieldMappingDnd();

  // Show placeholder when dragging from source and hovering over this container
  const showPlaceholder =
    activeSourceContainer === 'source' && overContainerId === campaignField;

  const hasFields = mappedFields.length > 0;
  const hasMultipleFields = mappedFields.length > 1;
  const willHaveMultipleFields = hasFields && showPlaceholder;
  const totalItemsWithPlaceholder =
    mappedFields.length + (showPlaceholder ? 1 : 0);

  const handleRemoveField = (crmFieldValue: string) => {
    if (onFieldRemoved && !disabled) {
      onFieldRemoved(campaignField, crmFieldValue);
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <Label
        required={required}
        className="text-sm font-medium text-neutral-700"
      >
        {fieldLabel}
      </Label>

      <div
        ref={setNodeRef}
        className={cn(
          'min-h-[80px] rounded-xl border-2 transition-all p-3 flex flex-col gap-2',
          isOver && 'border-primary-400 bg-primary-100 ring-4 ring-primary-100',
          !hasFields &&
            !showPlaceholder &&
            'border-dashed items-center justify-center bg-neutral-50',
          (hasFields || showPlaceholder) && 'bg-neutral-0 border-solid',
          (hasMultipleFields || willHaveMultipleFields) && 'pl-9',
        )}
      >
        <SortableContext
          id={campaignField}
          items={mappedFields.map((f) => f.value)}
          strategy={verticalListSortingStrategy}
        >
          {(() => {
            const effectiveInsertIndex =
              insertIndex >= 0 ? insertIndex : mappedFields.length;

            // Build the list with placeholder at the correct position
            const showMultipleLabels =
              hasMultipleFields || willHaveMultipleFields;

            return Array.from({ length: mappedFields.length + 1 }, (_, i) => {
              const elements: React.ReactNode[] = [];

              // Add placeholder at this position if needed
              if (showPlaceholder && i === effectiveInsertIndex) {
                const placeholderDisplayIndex =
                  totalItemsWithPlaceholder > 1 ? i : -1;
                elements.push(
                  <div key="placeholder" className="relative">
                    {placeholderDisplayIndex === 0 && (
                      <div className="absolute -left-4 top-1/2 -translate-y-1/2 -translate-x-1/2 text-[10px] font-bold text-primary-400 uppercase tracking-wider rotate-[-90deg]">
                        Primary
                      </div>
                    )}
                    {placeholderDisplayIndex > 0 && (
                      <div className="absolute -left-4 top-1/2 -translate-y-1/2 -translate-x-1/2 text-[10px] font-medium text-neutral-400 uppercase tracking-wider rotate-[-90deg]">
                        Alt {placeholderDisplayIndex}
                      </div>
                    )}
                    <div className="h-[52px] bg-neutral-200/50 border border-dashed border-primary/30 rounded-lg opacity-50" />
                  </div>,
                );
              }

              // Add the field at this position if it exists
              if (i < mappedFields.length) {
                const field = mappedFields[i];
                // Calculate display index (accounting for placeholder before this item)
                const displayIndex =
                  showPlaceholder && effectiveInsertIndex <= i ? i + 1 : i;

                elements.push(
                  <div key={field.value} className="relative">
                    {displayIndex === 0 && showMultipleLabels && (
                      <div className="absolute -left-4 top-1/2 -translate-y-1/2 -translate-x-1/2 text-[10px] font-bold text-primary-400 uppercase tracking-wider rotate-[-90deg]">
                        Primary
                      </div>
                    )}
                    {displayIndex > 0 && showMultipleLabels && (
                      <div className="absolute -left-4 top-1/2 -translate-y-1/2 -translate-x-1/2 text-[10px] font-medium text-neutral-400 uppercase tracking-wider rotate-[-90deg]">
                        Alt {displayIndex}
                      </div>
                    )}
                    <MappedFieldItem
                      field={field}
                      sampleContacts={sampleContacts}
                      onRemove={() => handleRemoveField(field.value)}
                      disabled={disabled}
                    />
                  </div>,
                );
              }

              return elements;
            });
          })()}
        </SortableContext>

        {!hasFields && !showPlaceholder && (
          <div
            className={cn(
              'text-sm font-medium pointer-events-none',
              isOver ? 'text-primary-600' : 'text-neutral-400',
            )}
          >
            {isOver ? 'Drop' : 'Drag'} field here
          </div>
        )}
      </div>
    </div>
  );
};
